import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLiveSettings } from "@/hooks/content";
import { useTranslatedList, useTranslatedRecord } from "@/hooks/useTranslatedList";
import { scheduleToday } from "@/mocks/schedule";

export default function LiveTVPage() {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);

  const { record: rawSettings } = useLiveSettings();
  const liveSettings = useTranslatedRecord(rawSettings, [
    "currentProgram",
    "liveTitle",
    "liveDescription",
    "nextProgram",
  ]);
  const isLive = liveSettings.sourceType !== "offline";
  const isOffline = liveSettings.sourceType === "offline";

  useEffect(() => {
    document.title = "L+TV LIVE | L+TV TÉLÉVISION";
  }, []);

  const toggleFullscreen = () => {
    if (!playerRef.current) return;
    if (!document.fullscreenElement) {
      playerRef.current.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const translatedSchedule = useTranslatedList(scheduleToday, ["program", "description"]);
  const liveNowItem = translatedSchedule.find((s) => s.live);

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-broadcast-line text-accent-400 text-lg"></i>
            <span className="text-accent-400 text-sm font-semibold uppercase tracking-wider">{t("live.liveTelevision")}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("live.title")}</h1>
          <p className="text-white/60 text-sm mt-1">{t("live.subtitle")}</p>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Player */}
          <div className="lg:col-span-2">
            <div
              ref={playerRef}
              className="relative bg-black rounded-lg overflow-hidden aspect-video"
            >
              {isLive && !isOffline ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20television%20broadcast%20control%20room%20live%20feed%20screen%2C%20multiple%20camera%20angles%20on%20monitors%2C%20modern%20TV%20studio%20live%20broadcasting%20scene%2C%20editorial%20photography%20style&width=1200&height=675&seq=live-player&orientation=landscape"
                    alt="L+TV Live Stream"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20"></div>
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={() => setIsPlaying(true)}
                        className="w-20 h-20 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm"
                      >
                        <i className="ri-play-fill text-white text-4xl ml-1"></i>
                      </button>
                    </div>
                  )}
                  <div className="absolute top-4 left-4 ltv-live-badge">
                    <i className="ri-broadcast-fill"></i>
                    LIVE
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-900/95">
                  <div className="w-20 h-20 rounded-full bg-primary-700 flex items-center justify-center mb-4">
                    <i className="ri-tv-line text-white text-3xl"></i>
                  </div>
                  <p className="text-white text-xl font-semibold mb-2">{t("live.offlineTitle")}</p>
                  <p className="text-white/60 text-sm text-center max-w-md px-6">
                    {liveSettings.liveDescription}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-white/50 text-sm">
                    <i className="ri-time-line"></i>
                    {t("live.nextBroadcast")}: {liveSettings.nextProgram}
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <i className={`ri-${isPlaying ? "pause" : "play"}-fill text-xl`}></i>
                  </button>
                  <button className="text-white/80 hover:text-white transition-colors">
                    <i className="ri-volume-up-line text-lg"></i>
                  </button>
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-accent-500 rounded-full"></div>
                  </div>
                  <span className="text-white/60 text-xs">LIVE</span>
                  <button
                    onClick={toggleFullscreen}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <i className={`ri-${isFullscreen ? "fullscreen-exit" : "fullscreen"}-line text-lg`}></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Info Bar */}
            <div className="mt-4 bg-white rounded-lg border border-background-200 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h2 className="font-bold text-foreground-900">{liveSettings.liveTitle}</h2>
                  <p className="text-sm text-foreground-600">{liveSettings.liveDescription}</p>
                </div>
                <div className="flex items-center gap-2">
                  {isLive && (
                    <span className="ltv-live-badge">
                      <i className="ri-broadcast-fill"></i>
                      LIVE
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Now Playing */}
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                {t("live.nowPlaying")}
              </h3>
              <div className="p-3 bg-primary-50 rounded-md border border-primary-100">
                <p className="font-semibold text-primary-900 text-sm">
                  {liveNowItem ? liveNowItem.program : liveSettings.currentProgram}
                </p>
                <p className="text-xs text-primary-600 mt-1">
                  {liveNowItem ? liveNowItem.description : liveSettings.liveDescription}
                </p>
              </div>
            </div>

            {/* Up Next */}
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                {t("live.upNext")}
              </h3>
              <p className="font-medium text-foreground-800 text-sm">
                {liveSettings.nextProgram}
              </p>
            </div>

            {/* Today's Schedule */}
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                {t("live.todaySchedule")}
              </h3>
              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {translatedSchedule.slice(0, 8).map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 p-2.5 rounded-md text-sm ${
                      item.live ? "bg-accent-50 border border-accent-100" : "bg-background-50"
                    }`}
                  >
                    <span className="font-mono text-foreground-500 text-xs whitespace-nowrap mt-0.5">
                      {item.time}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm truncate ${item.live ? "text-accent-600" : "text-foreground-800"}`}>
                        {item.program}
                      </p>
                      {item.live && (
                        <span className="ltv-live-badge text-[10px] mt-1">
                          <i className="ri-broadcast-fill"></i>
                          LIVE
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/schedule"
                className="text-sm text-accent-500 hover:text-accent-600 font-semibold mt-3 inline-flex items-center gap-1"
              >
                {t("live.fullSchedule")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}